import React, { useState, useMemo, useEffect } from 'react'
import Modal, { ModalHeader, ModalFooter } from '@app/components/Modal'
import * as S from '@app/components/Modal/styled'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import { GeneratedForms } from '../../../types/Templates'
import { useSnackbar } from '@app/snackbar'
import { fetch } from '../../../../common/fetch'
import getEnv from '@app/environments/getEnv'
import theme from '@app/ui/theme'
import { Eval, MITee } from '@app/evaluation-core/types'

type CustomSlopeTablesModalProps = {
  isOpen: boolean
  onClose: () => void
  evaluation: Eval | undefined
  templateId: string | undefined
  form: GeneratedForms | undefined
}

const CustomSlopeTablesModal: React.FC<CustomSlopeTablesModalProps> = ({
  isOpen,
  onClose,
  evaluation,
  templateId,
  form,
}) => {
  const [openSnackbar] = useSnackbar()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedTeeIndexes, setSelectedTeeIndexes] = useState<number[]>([])

  const tees: MITee[] = useMemo(
    () => evaluation?.holes?.[0]?.tees ?? [],
    [evaluation],
  )

  useEffect(() => {
    if (isOpen) {
      setSelectedTeeIndexes(tees.map((t) => t.teeIndex))
    }
  }, [isOpen, tees])

  const toggleTee = (teeIndex: number) => {
    setSelectedTeeIndexes((prev) =>
      prev.includes(teeIndex)
        ? prev.filter((i) => i !== teeIndex)
        : [...prev, teeIndex],
    )
  }

  const handleDownload = async () => {
    if (!templateId || !form || selectedTeeIndexes.length === 0) return
    setIsSubmitting(true)
    try {
      const response = await fetch.post<{ url: string }>(
        `${getEnv().api}/ratings/templates/${templateId}/forms/${form.id}/slope-tables/custom`,
        { teeIndexes: selectedTeeIndexes },
      )
      const url = response?.data?.url
      if (!url) throw new Error('No URL returned')

      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = ''
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      openSnackbar('Custom slope tables downloaded')
      onClose()
    } catch (error) {
      console.error('Error generating custom slope tables:', error)
      openSnackbar('Failed to generate custom slope tables')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="520px"
      height="auto"
      closeOnBackdropClick={true}
      showCloseButton={true}
    >
      <ModalHeader>
        <h2
          style={{
            height: '100%',
            paddingTop: '8px',
            margin: 0,
            fontSize: '20px',
            fontWeight: 600,
          }}
        >
          Custom Slope Tables
        </h2>
      </ModalHeader>
      <S.ModalScrollContent style={{ padding: '24px' }}>
        <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#555' }}>
          Pick the tees to include. Both men's and women's slope tables will be
          generated. The finalized rating itself is not affected.
        </p>
        {tees.length === 0 ? (
          <div style={{ padding: '20px', color: '#666', fontSize: '14px' }}>
            No tees available for this evaluation.
          </div>
        ) : (
          <div
            style={{
              border: '1px solid #e4e4e4',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            {tees.map((tee, idx) => {
              const checked = selectedTeeIndexes.includes(tee.teeIndex)
              return (
                <label
                  key={tee.id || tee.teeIndex}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 14px',
                    borderBottom:
                      idx === tees.length - 1 ? 'none' : '1px solid #eee',
                    cursor: 'pointer',
                    backgroundColor: checked ? '#f0f7ff' : 'white',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleTee(tee.teeIndex)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  {tee.color && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        backgroundColor: tee.color,
                        border: '1px solid #ccc',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '14px', fontWeight: 500 }}>
                      {tee.name || `Tee ${tee.teeNumber ?? tee.teeIndex + 1}`}
                    </span>
                    {tee.golfers && tee.golfers.length > 0 && (
                      <span style={{ fontSize: '12px', color: '#888' }}>
                        {tee.golfers.join(', ')}
                      </span>
                    )}
                  </div>
                </label>
              )
            })}
          </div>
        )}
      </S.ModalScrollContent>
      <ModalFooter>
        <RoundedButton
          title="Cancel"
          onPress={onClose}
          style={{
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.secondary}`,
            color: theme.colors.secondary,
          }}
          textStyle={{ color: theme.colors.secondary }}
        />
        <RoundedButton
          title={isSubmitting ? 'Generating...' : 'Download'}
          onPress={handleDownload}
          disabled={
            isSubmitting || selectedTeeIndexes.length === 0 || !form || !templateId
          }
          style={{
            backgroundColor: theme.colors.primary,
            marginLeft: '12px',
          }}
        />
      </ModalFooter>
    </Modal>
  )
}

export default CustomSlopeTablesModal
